/**
 * Slice 6 of Connection Diagnostics — `ConnectionTabFailureBanner`.
 *
 * When opening a tab against a database fails (`getPoolForDatabase`
 * threw a `DatabaseAccessError`), the host tab body shows this banner
 * above the regular content. Click "Show details" → opens the
 * `ConnectionDiagnosticsDialog` scoped to that database. The dismiss
 * × hides the banner for this failure (the host clears it on success).
 */

import { AlertTriangle, X } from 'lucide-react';
import { useAnalytics } from '../../../core/analytics';
import { ShowDiagnosticsButton } from './ShowDiagnosticsButton';

type ConnectionTabFailureBannerProps = {
  connectionId: string;
  database: string;
  /** Optional SQL error code captured from the structured failure shape. */
  sqlErrorCode?: number;
  /** When provided, renders a dismiss × that calls this back. */
  onDismiss?: () => void;
};

export function ConnectionTabFailureBanner({
  connectionId,
  database,
  sqlErrorCode,
  onDismiss,
}: ConnectionTabFailureBannerProps) {
  const { track } = useAnalytics();

  const handleDismiss = () => {
    track('feature:connection_failure_banner_dismissed', {
      hasSqlErrorCode: typeof sqlErrorCode === 'number',
    });
    onDismiss?.();
  };

  return (
    <div
      className="flex items-center gap-3 border-b border-destructive/30 bg-destructive/10 px-3 py-2 text-sm"
      data-testid="connection-tab-failure-banner"
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <span
        className="flex-1 truncate text-foreground"
        data-testid="connection-tab-failure-headline"
      >
        Couldn't open <code className="rounded bg-muted px-1">{database}</code>
        {typeof sqlErrorCode === 'number' ? ` (SQL error ${sqlErrorCode})` : '.'}
      </span>
      <ShowDiagnosticsButton
        connectionId={connectionId}
        trigger="pool_open_failure"
        scopeDatabase={database}
        data-testid="connection-tab-failure-show-details"
      />
      {onDismiss ? (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid="connection-tab-failure-dismiss"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
